import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Button,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  CircularProgress,
  LinearProgress,
  Divider,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCustomer from "../Customer/AddCustomer";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import CloseIcon from "@mui/icons-material/Close";
import {
  getAllCustomers,
  deleteCustomer,
  createCustomer,
  updateCustomer,
  importCustomer,
  downloadCustomerTemplate,
} from "../../service/customerService";
import { useSnackbar } from "../../App";
import AddIcon from "@mui/icons-material/Add";
import Color from "../../components/Color";
import { Download, Upload, UploadFile, Search } from "@mui/icons-material";

const Customers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { showSnackbar } = useSnackbar();

  const [customers, setCustomers] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 15, // Matches your backend's default per_page = 15
  });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const [sortModel, setSortModel] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [phoneError, setPhoneError] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    gst_number: "",
    contact_type: "",
  });

  // Import states
  const [importOpen, setImportOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);

  // Progress & Result states
  const [importProgressOpen, setImportProgressOpen] = useState(false);
  const [importSuccessOpen, setImportSuccessOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [progress, setProgress] = useState(0);
  const [currentCount, setCurrentCount] = useState(0);
  const [totalToImport, setTotalToImport] = useState(0);

  const handleFileSelect = (file) => {
    if (!file) return;
    const validTypes = [
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/csv",
    ];
    const validExtensions = [".csv", ".xlsx", ".xls"];
    const ext = "." + file.name.split(".").pop().toLowerCase();

    if (!validTypes.includes(file.type) && !validExtensions.includes(ext)) {
      showSnackbar("Please upload only CSV or Excel files (.xlsx, .xls, .csv)", "error");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showSnackbar("File size must be less than 10MB", "error");
      return;
    }

    setSelectedFile(file);
    showSnackbar(`Selected: ${file.name}`, "success");
  };

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const page = paginationModel.page + 1; // MUI DataGrid page starts from 0
      const sortField = sortModel[0]?.field || "id";
      const sortOrder = sortModel[0]?.sort || "desc";

      const res = await getAllCustomers(page, debouncedSearch.trim(), sortField, sortOrder);
      const data = res?.data;

      if (Array.isArray(data?.data)) {
        setCustomers(data.data);

        // Fix: Use pagination.total from backend response
        const paginationInfo = data?.pagination;
        const totalCount = paginationInfo?.total || 0;
        setTotal(totalCount);

        // Sync pageSize with backend's per_page (e.g., 15)
        if (paginationInfo?.per_page && paginationModel.pageSize !== paginationInfo.per_page) {
          setPaginationModel((prev) => ({
            ...prev,
            pageSize: paginationInfo.per_page,
          }));
        }

        // Removed the snackbar here to avoid duplication with the overlay message
      } else {
        showSnackbar("Unexpected response format!", "error");
        setCustomers([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Fetch customers error:", error);
      showSnackbar("Failed to fetch customers!", "error");
      setCustomers([]);
      setTotal(0);
    }
    setLoading(false);
  }, [paginationModel.page, debouncedSearch, sortModel, showSnackbar]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  const handleSubmit = async (data) => {
    try {
      setPhoneError("");
      if (editingId) {
        await updateCustomer(editingId, data);
        showSnackbar("Customer updated successfully!", "success");
      } else {
        await createCustomer(data);
        showSnackbar("Customer added successfully!", "success");
      }
      resetForm();
      fetchCustomers();
    } catch (error) {
      const errorMessage = error.response?.data?.errors?.phone?.[0];
      if (errorMessage) {
        setPhoneError(errorMessage);
        showSnackbar(errorMessage, "error");
      } else {
        showSnackbar(error.response?.data?.message || "Failed to save customer!", "error");
      }
      throw error;
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      address: "",
      gst_number: "",
      contact_type: "",
    });
    setPhoneError("");
    setEditingId(null);
    setOpenModal(false);
  };

  const handleEdit = (customer) => {
    setFormData({
      name: customer.name || "",
      email: customer.email || "",
      phone: customer.phone || "",
      address: customer.address || "",
      gst_number: customer.gst_number || "",
      contact_type: customer.contact_type || "",
    });
    setEditingId(customer.id);
    setOpenModal(true);
    handleMenuClose();
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteCustomer(deleteId);
      showSnackbar("Customer deleted successfully!", "success");
      fetchCustomers();
    } catch {
      showSnackbar("Failed to delete customer!", "error");
    } finally {
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  };

  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleClear = () => {
    setSearch("");
    setDebouncedSearch("");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      flex: 0.7,
      minWidth: isMobile ? 80 : 130,
      renderCell: (params) => (
        <IconButton
          onClick={(e) => handleMenuOpen(e, params.row)}
          sx={{
            backgroundColor: "rgba(0,0,0,0.04)",
            borderRadius: 1.5,
            padding: isMobile ? "10px" : "8px",
            "&:hover": { backgroundColor: "rgba(0,0,0,0.08)" },
          }}
        >
          <MoreVertIcon sx={{ fontSize: isMobile ? 18 : 16 }} />
        </IconButton>
      ),
    },
    { field: "name", headerName: "Customer/Company Name", flex: 1, minWidth: isMobile ? 120 : 200 },
    {
      field: "phone",
      headerName: "Contact",
      flex: 1,
      minWidth: isMobile ? 120 : 150,
      renderCell: (params) => {
        const contactType = params.row.contact_type?.toLowerCase();
        let icon;
        switch (contactType) {
          case "whatsapp":
            icon = <WhatsAppIcon sx={{ color: "#25D366", mr: 1, fontSize: isMobile ? 18 : 20 }} />;
            break;
          case "mobile":
            icon = <PhoneIcon sx={{ color: "#1976d2", mr: 1, fontSize: isMobile ? 18 : 20 }} />;
            break;
          case "email":
            icon = <EmailIcon sx={{ color: "#d93025", fontSize: isMobile ? 18 : 20 }} />;
            break;
          default:
            icon = <PhoneIcon sx={{ color: "#1976d2", mr: 1, fontSize: isMobile ? 18 : 20 }} />;
        }
        return (
          <Box display="flex" alignItems="center" mt={2}>
            {icon}
            <Typography sx={{ fontSize: isMobile ? "12px" : "14px" }}>{params.row.phone}</Typography>
          </Box>
        );
      },
    },
    { field: "address", headerName: "Address", flex: 1.5, minWidth: 200 },
    {
      field: "gst_number",
      headerName: "GST Number",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => params.row.gst_number || "-",
    },
  ];

  const CustomNoRowsOverlay = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        p: 2,
      }}
    >
      <Typography variant="h6" color="text.secondary">
        {debouncedSearch
          ? "No customer found"
          : "No customers available. Add one to get started!"}
      </Typography>
    </Box>
  );

  return (
    <Box mb={{ xs: 0, sm: 1 }} sx={{ padding: isMobile ? 1 : 0 }}>
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        justifyContent={isMobile ? "stretch" : { sm: "flex-start", md: "flex-start", lg: "space-between" }}
        alignItems={isMobile ? "stretch" : "center"}
        flexWrap="wrap"
        gap={isMobile ? 1 : 2}
        mt={2}
      >
        <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 1 : "10px", width: isMobile ? "100%" : "auto" }}>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search customer"
            variant="outlined"
            InputLabelProps={{ shrink: false }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: isMobile ? 18 : 20 }} />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClear}>
                    <CloseIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputLabel-root": { display: "none" },
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
                height: isMobile ? "34px" : "37.69px",
                fontSize: isMobile ? "0.85rem" : "1rem",
              },
              width: isMobile ? "100%" : { sm: 180, md: 220, lg: 250 },
            }}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? 1 : 2, width: isMobile ? "100%" : "auto" }}>
          <Button
            sx={{
              background: Color.info.main,
              color: "white",
              height: isMobile ? "34px" : "37.69px",
              width: isMobile ? "100%" : { sm: 190, md: 200, lg: 250 },
              whiteSpace: "nowrap",
              fontSize: isMobile ? "0.85rem" : "1rem",
              "&:hover": { background: Color.info.dark },
            }}
            onClick={() => setImportOpen(true)}
            startIcon={<UploadFile />}
          >
            Import Customer
          </Button>
          <Button
            sx={{
              background: Color.primary.main,
              color: "white",
              height: isMobile ? "34px" : "37.69px",
              width: isMobile ? "100%" : { sm: 170, md: 180, lg: 240 },
              whiteSpace: "nowrap",
              fontSize: isMobile ? "0.85rem" : "1rem",
              "&:hover": { background: Color.primary.dark },
            }}
            onClick={() => {
              setEditingId(null);
              setOpenModal(true);
            }}
            startIcon={<AddIcon />}
          >
            Add Customer
          </Button>
        </Box>
      </Box>

      <Box sx={{ width: "100%", mt: isMobile ? 2 : 2 }}>
        <DataGrid
          rows={customers}
          columns={columns.map((col) => ({
            ...col,
            flex: col.flex || 1,
            minWidth: col.minWidth || (isMobile ? 80 : 120),
          }))}
          getRowId={(row) => row.id}
          autoHeight
          rowHeight={isMobile ? 48 : 56}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={total}
          paginationMode="server"
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          disableRowSelectionOnClick
          disableColumnMenu
              disableColumnSorting
          components={{
            NoRowsOverlay: CustomNoRowsOverlay,
          }}
          sx={{
            fontFamily: '"Poppins", sans-serif',
            borderRadius: "10px",
            "& .MuiDataGrid-columnHeaderTitle": { fontSize: isMobile ? "14px" : "16px", fontWeight: 600, pl: 1 },
            "& .MuiDataGrid-cell": { fontSize: isMobile ? "12px" : "14px", pl: 2 },
            "& .MuiDataGrid-row:nth-of-type(even)": { backgroundColor: "#fff" },
            "& .MuiDataGrid-row:nth-of-type(odd)": { backgroundColor: "#f4f4f4" },
            "& .MuiLinearProgress-bar": { backgroundColor: Color.primary.main },
            "& .MuiLinearProgress-colorPrimary": { backgroundColor: Color.secondary.main },
            overflowX: isMobile ? "hidden" : "auto",
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none",
              border: "2px solid",
              borderColor: Color.primary.main,
              borderRadius: "4px",
            },
            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
              outline: "none",
              border: "2px solid",
              borderColor: Color.primary.main,
              borderRadius: "4px",
            },
          }}
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        PaperProps={{ style: { maxHeight: 200, width: isMobile ? "90vw" : 200 } }}
      >
        <MenuItem onClick={() => handleEdit(selectedRow)} sx={{ fontSize: isMobile ? "0.85rem" : "1rem", padding: isMobile ? "10px" : "8px" }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => handleDeleteClick(selectedRow?.id)} sx={{ fontSize: isMobile ? "0.85rem" : "1rem", padding: isMobile ? "10px" : "8px" }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1, color: "error.main" }} /> Delete
        </MenuItem>
      </Menu>

      <AddCustomer
        open={openModal}
        onClose={resetForm}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        phoneError={phoneError}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontSize: isMobile ? "1rem" : "1.25rem" }}>Delete Confirmation</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: isMobile ? "0.85rem" : "1rem" }}>
            Are you sure you want to delete the customer <strong>{selectedRow?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ mr: 2, mb: 2, flexDirection: isMobile ? "column" : "row", gap: isMobile ? 1 : 0 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              border: "1px solid",
              borderColor: Color.secondary.main,
              color: Color.label.main,
              width: isMobile ? "100%" : "auto",
              fontSize: isMobile ? "0.85rem" : "1rem",
              padding: isMobile ? "8px" : "6px 16px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            sx={{
              background: Color.primary.main,
              color: "white",
              width: isMobile ? "100%" : "auto",
              fontSize: isMobile ? "0.85rem" : "1rem",
              padding: isMobile ? "8px" : "6px 16px",
              "&:hover": { background: Color.primary.dark },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== BULK IMPORT DIALOG (UNCHANGED) ==================== */}
      <Dialog
        open={importOpen}
        onClose={() => {
          setImportOpen(false);
          setSelectedFile(null);
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3, m: isMobile ? 1 : 3, maxWidth: 580 } }}
      >
        <DialogTitle sx={{ fontSize: "1.5rem", fontWeight: 600, pb: 1 }}>
          Bulk Import Customers
          <IconButton
            onClick={() => {
              setImportOpen(false);
              setSelectedFile(null);
            }}
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 4, pb: 2 }}>
          <Box sx={{ bgcolor: "#f0f7ff", borderRadius: 2, p: 2.5, mb: 3, borderLeft: "5px solid", borderColor: Color.primary.main }}>
            <Typography variant="subtitle1" fontWeight={600} color="primary" gutterBottom>
              How to Import Customers
            </Typography>
            <Typography variant="body2" color="text.secondary" component="ol" sx={{ pl: 2.5, m: 0 }}>
              <li>Download the Excel/CSV template using the button below</li>
              <li>Fill in your customer data following the template format</li>
              <li>Upload the completed file to import all customers at once</li>
            </Typography>
          </Box>

          <Button
            variant="outlined"
            fullWidth
            startIcon={<Download />}
            sx={{
              height: 52,
              borderColor: Color.primary.main,
              color: Color.primary.main,
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 2,
              mb: 3,
              "&:hover": { borderColor: Color.primary.dark, bgcolor: "rgba(124, 11, 204, 0.04)" },
            }}
            onClick={async () => {
              try {
                const res = await downloadCustomerTemplate();
                const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "Customer_Import_Template.csv";
                a.click();
                window.URL.revokeObjectURL(url);
                showSnackbar("Template downloaded successfully!", "success");
              } catch (err) {
                showSnackbar("Failed to download template", "error");
              }
            }}
          >
            DOWNLOAD EXCEL/CSV TEMPLATE
          </Button>

          {selectedFile && (
            <Box sx={{ bgcolor: "success.light", color: "white", px: 3, py: 1.5, borderRadius: 2, mb: 2, textAlign: "center" }}>
              <Typography variant="body1" fontWeight={600}>Selected: {selectedFile.name}</Typography>
              <Typography variant="caption">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</Typography>
            </Box>
          )}

          <Box
            sx={{
              border: "2px dashed",
              borderColor: selectedFile ? "success.main" : "#90caf9",
              borderRadius: 2,
              bgcolor: selectedFile ? "#f0fff4" : "#f8fbff",
              height: 200,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.3s",
              "&:hover": {
                bgcolor: selectedFile ? "#e8f5e8" : "#e3f2fd",
                borderColor: selectedFile ? "success.dark" : "#42a5f5",
              },
            }}
            onClick={() => document.getElementById("import-file-input")?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const file = e.dataTransfer.files[0];
              if (file) handleFileSelect(file);
            }}
          >
            <UploadFile sx={{ fontSize: 60, color: selectedFile ? "success.main" : "#42a5f5", mb: 1 }} />
            <Typography variant="h6" fontWeight={600} color={selectedFile ? "success.main" : "inherit"}>
              {selectedFile ? "Ready to Import!" : "Click or drag file here"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Supports: .xlsx, .xls, .csv • Max 10MB
            </Typography>
          </Box>

          <input
            id="import-file-input"
            type="file"
            accept=".xlsx,.xls,.csv,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 4, pb: 4, gap: 2, justifyContent: "space-between" }}>
          <Button
            onClick={() => {
              setImportOpen(false);
              setSelectedFile(null);
            }}
            sx={{
              minWidth: 100,
              height: 40,
              border: "1px solid #ddd",
              color: "text.primary",
              fontWeight: 500,
              textTransform: "none",
              borderRadius: 2,
            }}
          >
            CANCEL
          </Button>

          <Button
            variant="contained"
            startIcon={importing ? <CircularProgress size={20} /> : <Upload />}
            disabled={!selectedFile || importing}
            sx={{ minWidth: 160, bgcolor: selectedFile ? Color.primary.main : "#e0e0e0", color: "white" }}
            onClick={async () => {
              if (!selectedFile) return;

              setImporting(true);
              setImportProgressOpen(true);
              setProgress(0);
              setCurrentCount(0);
              setTotalToImport(0);
              setImportResult(null);

              try {
                const response = await importCustomer(selectedFile);
                const result = response?.data?.data || response?.data;

                const total = result.total_rows || 0;
                const success = result.success_count || 0;
                const failed = result.failed_count || 0;

                setTotalToImport(total);
                setImportResult({
                  total_rows: total,
                  success_count: success,
                  failed_count: failed,
                });

                let count = 0;
                const interval = setInterval(() => {
                  count += Math.floor(Math.random() * 18) + 8;
                  if (count >= total) {
                    count = total;
                    clearInterval(interval);
                  }
                  setCurrentCount(count);
                  setProgress((count / total) * 100);
                }, 70);

                setTimeout(() => {
                  clearInterval(interval);
                  setCurrentCount(total);
                  setProgress(100);
                  setTimeout(() => {
                    setImportProgressOpen(false);
                    setImportSuccessOpen(true);
                    fetchCustomers();
                  }, 600);
                }, total > 100 ? 2500 : 1800);

                showSnackbar(`"${selectedFile.name}" imported successfully!`, "success");
              } catch (error) {
                setImportProgressOpen(false);

                const responseData = error?.response?.data;
                const data = responseData?.data || {};
                const message = responseData?.message || "Import failed!";

                const failedRows = data.failed_rows || [];
                const totalFailed = data.failed_count || 0;
                const totalRows = data.total_rows || 0;

                const isDuplicatePhoneError =
                  failedRows.length > 0 &&
                  failedRows.every((row) =>
                    row.errors.every((err) =>
                      err.toLowerCase().includes("phone number already exists")
                    )
                  );

                if (isDuplicatePhoneError && totalFailed > 0) {
                  const duplicatePhones = {};
                  failedRows.forEach((item) => {
                    item.errors.forEach((err) => {
                      const match = err.match(/Phone number already exists: (\d+)/);
                      if (match) {
                        const phone = match[1];
                        if (!duplicatePhones[phone]) duplicatePhones[phone] = [];
                        duplicatePhones[phone].push(item.row);
                      }
                    });
                  });

                  setImportResult({
                    isDuplicateError: true,
                    duplicateGroups: duplicatePhones,
                    totalRows: totalRows,
                    failedCount: totalFailed,
                    successCount: data.success_count || 0,
                  });
                  setImportSuccessOpen(true);
                  showSnackbar("Duplicate phone numbers found! Import stopped.", "warning");
                } else {
                  setImportResult({
                    total_rows: totalRows,
                    success_count: data.success_count || 0,
                    failed_count: totalFailed,
                  });
                  setImportSuccessOpen(true);
                  showSnackbar(message, "error");
                }
              } finally {
                setImporting(false);
                setSelectedFile(null);
                setImportOpen(false);
              }
            }}
          >
            {importing ? "Importing..." : "Import Customers"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Progress Dialog & Result Dialog — unchanged */}
      <Dialog open={importProgressOpen} hideBackdrop PaperProps={{ sx: { borderRadius: 3, width: 440, maxWidth: "95vw", boxShadow: 24 } }}>
        <DialogContent sx={{ textAlign: "center", py: 6 }}>
          <CircularProgress size={80} thickness={5} sx={{ color: Color.primary.main, mb: 3 }} />
          <Typography variant="h6" fontWeight={600} gutterBottom>Importing Customers...</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>Please wait while we process your file</Typography>

          <Box sx={{ width: "100%", px: 4 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="h5" fontWeight="bold">{currentCount} / {totalToImport || "..."}</Typography>
              <Typography variant="h5" fontWeight="bold" color="primary">{progress.toFixed(0)}%</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ height: 12, borderRadius: 2, bgcolor: "#e0e0e0", "& .MuiLinearProgress-bar": { bgcolor: Color.primary.main } }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={importSuccessOpen} onClose={() => setImportSuccessOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
          {importResult?.isDuplicateError ? (
            <>
              <Typography variant="h5" fontWeight={700} color="error.main" gutterBottom>
                Duplicate Phone Numbers Found!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Import stopped to prevent data conflict
              </Typography>
            </>
          ) : (
            <Typography variant="h6" fontWeight={600}>Import Completed!</Typography>
          )}
        </DialogTitle>

        <DialogContent sx={{ px: 4 }}>
          {importResult?.isDuplicateError ? (
            <Box sx={{ my: 3 }}>
              <Grid container spacing={2} sx={{ textAlign: "center", mb: 4, justifyContent: "center" }}>
                <Grid item xs={4}>
                  <Typography variant="h5" fontWeight="bold">{importResult.totalRows || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Rows</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="h5" fontWeight="bold" color="success.main">{importResult.successCount || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Imported</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="h5" fontWeight="bold" color="error.main">{importResult.failedCount || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Failed</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" fontWeight={600} gutterBottom>
                Duplicate Phone Numbers:
              </Typography>

              <Box sx={{ maxHeight: 300, overflowY: "auto", mt: 2 }}>
                {Object.entries(importResult.duplicateGroups || {}).map(([phone, rows]) => (
                  <Box key={phone} sx={{ bgcolor: "error.light", color: "white", borderRadius: 2, p: 2, mb: 2, textAlign: "center" }}>
                    <Typography variant="h6" fontWeight="bold">{phone}</Typography>
                    <Typography variant="body2">
                      Found in row(s): <strong>{rows.sort((a, b) => a - b).join(", ")}</strong> of your file
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Alert severity="warning" sx={{ mt: 3 }}>
                <strong>{importResult.failedCount} customers</strong> were blocked due to duplicate phone numbers.
                Please remove or update them and try again.
              </Alert>
            </Box>
          ) : (
            <Grid container spacing={10} sx={{ textAlign: "center", mt: 2 }}>
              <Grid item xs={12}><Typography fontWeight="bold" color="black">{importResult?.total_rows || 0}</Typography><Typography color="text.secondary">Total Rows</Typography></Grid>
              <Grid item xs={12}><Typography color="success.main" fontWeight="bold">{importResult?.success_count || 0}</Typography><Typography color="text.secondary">Successfully Imported</Typography></Grid>
              <Grid item xs={12}><Typography color="error.main" fontWeight="bold">{importResult?.failed_count || 0}</Typography><Typography color="text.secondary">Failed</Typography></Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pb: 4 }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => setImportSuccessOpen(false)}
            sx={{
              minWidth: 200,
              bgcolor: importResult?.isDuplicateError ? "error.main" : Color.primary.main,
              "&:hover": {
                bgcolor: importResult?.isDuplicateError ? "error.dark" : Color.primary.dark,
              },
            }}
          >
            {importResult?.isDuplicateError ? "Fix File & Retry" : "Done"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Customers;